import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useQuota } from '../../context/QuotaContext';
import { navigationColors, authColors, shadows } from '../../styles/colors';
import { navigateToPricing } from '../../utils/navigationUtils';
import {
  LayoutDashboard,
  FolderOpen,
  Settings,
  Users,
  Mail,
  LogOut,
  DollarSign,
  Calendar,
  Sparkles,
  ChevronRight,
  MessageCircle,
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import Tooltip from '../ui/Tooltip';

/** Intentional hover: open after dwell, close a bit slower to tolerate edge slips. */
const EXPAND_OPEN_DELAY_MS = 200;
const EXPAND_CLOSE_DELAY_MS = 300;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Block nav clicks during guided demo tour */
  interactionLocked?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, interactionLocked = false }) => {
  const { user, signOut } = useAuth();
  const { userPlan } = useQuota();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const sidebarPanelRef = React.useRef<HTMLDivElement>(null);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExpandTimer = useCallback(() => {
    if (expandTimerRef.current != null) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, []);

  const scheduleExpand = useCallback(
    (expanded: boolean, delayMs: number) => {
      clearExpandTimer();
      expandTimerRef.current = setTimeout(() => {
        setIsExpanded(expanded);
        expandTimerRef.current = null;
      }, delayMs);
    },
    [clearExpandTimer]
  );

  const collapseNow = useCallback(() => {
    clearExpandTimer();
    setIsExpanded(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement && sidebarPanelRef.current?.contains(active)) {
      active.blur();
    }
  }, [clearExpandTimer]);

  // Alt-tab: mouseleave never fires; focus on a clicked nav link also re-expands on return
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') collapseNow();
    };
    window.addEventListener('blur', collapseNow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', collapseNow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [collapseNow]);

  useEffect(() => {
    if (interactionLocked) collapseNow();
  }, [interactionLocked, collapseNow]);

  useEffect(() => () => clearExpandTimer(), [clearExpandTimer]);

  const planName = userPlan?.name ?? null;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sidebar: Logout error:', error);
    }
  };

  const handleUpgradeClick = () => {
    onClose();
    navigateToPricing(navigate);
  };

  const navigation = [
    {
      name: t('nav.dashboard'),
      href: '/app',
      icon: LayoutDashboard,
    },
    {
      name: t('nav.calendar'),
      href: '/app/calendar',
      icon: Calendar,
    },
    {
      name: t('nav.leads'),
      href: '/app/leads',
      icon: Users,
    },
    {
      name: t('nav.messages'),
      href: '/app/messages',
      icon: Mail,
    },
    {
      name: t('nav.projects'),
      href: '/app/projects',
      icon: FolderOpen,
    },
    {
      name: t('nav.finances'),
      href: '/app/finances',
      icon: DollarSign,
    },
  ];

  const isActive = (href: string) => {
    if (href === '/app') {
      return location.pathname === '/app' || location.pathname === '/app/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Mobile/Tablet backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar — desktop only; keep fully unmounted from layout paint on mobile
          (off-screen -translate-x-full + border/shadow can leak a 1px dark seam). */}
      <div
        className={`
          hidden md:block
          fixed inset-y-0 left-0 z-50 pointer-events-none
          transform transition-transform duration-300 ease-out
          w-60
          md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <div
          ref={sidebarPanelRef}
          className={`h-full md:w-20 ${isExpanded && !interactionLocked ? 'md:w-60' : ''} ${navigationColors.sidebar.background} border-r ${navigationColors.sidebar.border} shadow-2xl transition-[width] duration-300 ease-out ${
            interactionLocked ? 'pointer-events-none' : 'pointer-events-auto'
          }`}
          aria-hidden={interactionLocked || undefined}
          onMouseEnter={() => {
            if (interactionLocked) return;
            scheduleExpand(true, EXPAND_OPEN_DELAY_MS);
          }}
          onMouseLeave={() => scheduleExpand(false, EXPAND_CLOSE_DELAY_MS)}
          onFocus={(e) => {
            if (interactionLocked) return;
            // Mouse click leaves focus on the link without :focus-visible — don't keep sidebar open
            const target = e.target as HTMLElement;
            if (typeof target.matches === 'function' && target.matches(':focus-visible')) {
              clearExpandTimer();
              setIsExpanded(true);
            }
          }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              scheduleExpand(false, EXPAND_CLOSE_DELAY_MS);
            }
          }}
        >
        <div className="flex flex-col h-full">
          {/* Header - Logo & Brand */}
          <div className={`px-3 py-5 border-b ${navigationColors.sidebar.borderHeader} relative`}>
            {/* Même structure que les nav items : px-3 + w-8 pour aligner les icônes */}
            <div className="flex items-center px-3">
              {/* Logo dans un conteneur fixe - même w-8 que les nav items */}
              <span className="w-8 flex items-center justify-center flex-shrink-0">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 32 32" 
                  width="24" 
                  height="24"
                  className="text-blue-500 transition-transform duration-300 hover:scale-110"
                >
                  <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.8"/>
                  <circle cx="16" cy="16" r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6"/>
                  <circle cx="16" cy="16" r="6" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.4"/>
                  <circle cx="16" cy="16" r="2" fill="currentColor"/>
                </svg>
                <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full pointer-events-none"></div>
              </span>
              
              {/* Brand mark — fixed blue, never follows user accent */}
              <Link
                to="/"
                className={`
                  font-bold text-blue-400 text-xl tracking-tight 
                  hover:text-blue-300 transition-all duration-300 ease-out
                  whitespace-nowrap overflow-hidden
                  ${!isExpanded
                    ? 'max-w-0 opacity-0 ml-0' 
                    : 'max-w-[180px] opacity-100 ml-2'
                  }
                `}
              >
                Leadsme
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const active = isActive(item.href);
              const navLink = (
                <NavLink
                  to={item.href}
                  end={item.href === '/app'}
                  onClick={onClose}
                  className={`
                    group relative flex items-center
                    justify-start px-3
                    py-3 rounded-xl
                    text-sm font-medium
                    transition-all duration-300 ease-out
                    ${active
                      ? `${navigationColors.active.background} ${navigationColors.active.text} ${navigationColors.active.shadow}`
                      : `${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}`
                    }
                  `}
                >
                  {/* Active indicator */}
                  {active && (
                    <div 
                      className={`
                        absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full
                        transition-opacity duration-300
                        ${!isExpanded ? 'opacity-0' : 'opacity-100'}
                      `}
                    />
                  )}
                  
                  {/* Icône dans un conteneur fixe pour éviter qu'elle bouge */}
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <item.icon className={`
                      w-5 h-5
                      transition-transform duration-200 ease-out
                      ${active ? 'scale-110' : 'group-hover:scale-110'}
                    `} />
                  </span>
                  
                  {/* Texte avec animation smooth */}
                  <span 
                    className={`
                      whitespace-nowrap overflow-hidden
                      transition-all duration-300 ease-out
                      ${!isExpanded
                        ? 'max-w-0 opacity-0 ml-0' 
                        : 'max-w-[180px] opacity-100 ml-2'
                      }
                    `}
                  >
                    {item.name}
                  </span>
                </NavLink>
              );

              return <React.Fragment key={item.name}>{navLink}</React.Fragment>;
            })}
          </nav>

          {/* Plan Indicator / Upgrade Banner */}
          {planName && (
            <div className="px-3 pb-3">
              {planName === 'free' ? (
                <button
                  onClick={handleUpgradeClick}
                  className={`w-full relative overflow-hidden rounded-xl ${authColors.primarySolid} transition-all duration-300 ease-out ${shadows.colored.blue} hover:scale-[1.02] group px-3 py-3 flex items-center`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  {/* Icône dans un conteneur fixe - même w-8 que les nav items */}
                  <span className="w-8 flex items-center justify-center flex-shrink-0 relative z-10">
                    <Sparkles className="w-5 h-5 text-white" />
                  </span>
                  
                  {/* Texte avec animation smooth */}
                  <span 
                    className={`
                      flex items-center justify-between flex-1 overflow-hidden
                      transition-all duration-300 ease-out
                      ${!isExpanded
                        ? 'max-w-0 opacity-0 ml-0' 
                        : 'max-w-[180px] opacity-100 ml-2'
                      }
                    `}
                  >
                    <span className="text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap">
                      {t('nav.upgradeToPro')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-white/80 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" />
                  </span>
                </button>
              ) : (
                <div className={`w-full rounded-xl ${authColors.primarySolid} ${shadows.medium} px-3 py-3 flex items-center`}>
                  {/* Icône dans un conteneur fixe - même w-8 que les nav items */}
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </span>
                  
                  {/* Texte avec animation smooth */}
                  <span 
                    className={`
                      overflow-hidden whitespace-nowrap
                      transition-all duration-300 ease-out
                      ${!isExpanded
                        ? 'max-w-0 opacity-0 ml-0' 
                        : 'max-w-[180px] opacity-100 ml-2'
                      }
                    `}
                  >
                    <span className="text-xs font-bold text-white uppercase tracking-wider">
                      {t('settings.planPro')}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Footer - User Profile & Actions */}
          <div className={`border-t ${navigationColors.sidebar.borderHeader} p-3 space-y-2`}>
            {/* User Profile */}
            <div className="flex items-center px-1 py-2.5 rounded-xl transition-all duration-300 ease-out">
              {/* Avatar dans un conteneur fixe - assez large pour l'avatar + ring */}
              <div className="w-12 flex items-center justify-center flex-shrink-0">
                <div className="relative rounded-full p-0.5 ring-2 ring-primary-500/20">
                  <UserAvatar user={user} sizeClassName="w-9 h-9" />
                </div>
              </div>
              
              {/* Info utilisateur avec animation smooth */}
              <div 
                className={`
                  min-w-0 overflow-hidden
                  transition-all duration-300 ease-out
                  ${!isExpanded
                    ? 'max-w-0 opacity-0 ml-0' 
                    : 'max-w-[180px] opacity-100 ml-3'
                  }
                `}
              >
                <p className="text-sm font-medium text-white truncate">
                  {user?.user_metadata?.name || user?.email || 'User'}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {user?.email || ''}
                </p>
              </div>
            </div>
            
            {/* Settings & Logout */}
            {(() => {
              const settingsLink = (
                <NavLink
                  to="/app/settings"
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center justify-start px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ease-out group ${
                      isActive
                        ? `${navigationColors.active.background} ${navigationColors.active.text} ${navigationColors.active.shadow}`
                        : `${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}`
                    }`
                  }
                >
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <Settings className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
                  </span>
                  <span 
                    className={`
                      whitespace-nowrap overflow-hidden
                      transition-all duration-300 ease-out
                      ${!isExpanded
                        ? 'max-w-0 opacity-0 ml-0' 
                        : 'max-w-[180px] opacity-100 ml-2'
                      }
                    `}
                  >
                    {t('nav.settings')}
                  </span>
                </NavLink>
              );

              const feedbackLink = (
                <Tooltip
                  content={t('nav.writeToTeam')}
                  position="right"
                  disabled={isExpanded}
                >
                  <Link
                    to="/contact"
                    onClick={onClose}
                    className={`flex items-center justify-start px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ease-out group ${navigationColors.inactive.text} ${navigationColors.inactive.hoverText} ${navigationColors.inactive.hoverBackground}`}
                  >
                    <span className="w-8 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                    </span>
                    <span
                      className={`
                        whitespace-nowrap overflow-hidden
                        transition-all duration-300 ease-out
                        ${!isExpanded
                          ? 'max-w-0 opacity-0 ml-0'
                          : 'max-w-[180px] opacity-100 ml-2'
                        }
                      `}
                    >
                      {t('nav.writeToTeam')}
                    </span>
                  </Link>
                </Tooltip>
              );

              const logoutButton = (
                <button
                  onClick={handleSignOut}
                  className={`
                    flex items-center justify-start px-3 w-full
                    py-2.5 rounded-xl text-sm font-medium
                    text-slate-400 hover:text-red-400
                    hover:bg-red-500/10
                    transition-all duration-300 ease-out
                    group
                  `}
                >
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                  <span 
                    className={`
                      whitespace-nowrap overflow-hidden
                      transition-all duration-300 ease-out
                      ${!isExpanded
                        ? 'max-w-0 opacity-0 ml-0' 
                        : 'max-w-[180px] opacity-100 ml-2'
                      }
                    `}
                  >
                    {t('common.logout')}
                  </span>
                </button>
              );

              return (
                <>
                  {feedbackLink}
                  {settingsLink}
                  {logoutButton}
                </>
              );
            })()}
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
